import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Download, Trash2, History, RotateCcw, PenLine } from "lucide-react";
import { SendSignatureDialog } from "@/components/Signatures";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/hooks/useRefData";
import { inputClass } from "@/components/FieldInput";
import { Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = ["Atto notarile", "Atto istitutivo", "Documento d'identità", "Contratto", "Contabilità", "Fiscale", "Antiriciclaggio", "Corrispondenza", "Verbale", "Altro"];
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function DocumentsPanel({ trustId }) {
  const { can, user, hasRole } = useAuth();
  const [signDoc, setSignDoc] = useState(null);
  const refs = useRefData(trustId ? [] : ["trusts"]);
  const [docs, setDocs] = useState([]);
  const [trash, setTrash] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState(null);
  const fileRef = useRef();
  const verRef = useRef();
  const [verTarget, setVerTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/documents", { params: { ...(trustId ? { trust_id: trustId } : {}), deleted: trash } })
      .then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [trustId, trash]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !form.title || !form.category) return toast.error("Seleziona file, titolo e categoria");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    const tid = trustId || form.trust_id;
    if (tid) fd.append("trust_id", tid);
    if (form.notes) fd.append("notes", form.notes);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("Documento caricato");
      setOpen(false);
      setForm({});
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadVersion = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !verTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/documents/${verTarget.id}/version`, fd);
      toast.success(`Versione ${data.version} caricata`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  const trustName = (id) => (refs.trusts || []).find((t) => t.id === id)?.name || "—";

  return (
    <Card data-testid="documents-panel">
      <div className="flex flex-wrap gap-2 items-center justify-between p-4 border-b border-slate-100">
        <div className="flex gap-2">
          <Button size="sm" variant={trash ? "outline" : "default"} className={!trash ? "bg-[#0A2540] text-white hover:bg-[#031B33]" : ""} onClick={() => setTrash(false)} data-testid="documents-active-tab">Archivio</Button>
          {user?.role === "admin" && (
            <Button size="sm" variant={trash ? "default" : "outline"} className={trash ? "bg-[#0A2540] text-white hover:bg-[#031B33]" : ""} onClick={() => setTrash(true)} data-testid="documents-trash-tab">Cestino (ripristino)</Button>
          )}
        </div>
        {can("documents", "c") && !trash && (
          <Button size="sm" data-testid="document-upload-open" className="bg-[#0A2540] hover:bg-[#031B33] text-white" onClick={() => setOpen(true)}><Upload className="h-4 w-4 mr-1" />Carica documento</Button>
        )}
      </div>
      <input type="file" ref={verRef} className="hidden" onChange={uploadVersion} data-testid="document-version-input" />
      {signDoc && <SendSignatureDialog doc={signDoc} onClose={() => setSignDoc(null)} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left" data-testid="documents-table">
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Titolo</th><th className="px-4 py-3">Categoria</th>{!trustId && <th className="px-4 py-3">Trust</th>}<th className="px-4 py-3">File</th><th className="px-4 py-3">Ver.</th><th className="px-4 py-3">Caricato</th><th className="px-4 py-3 text-right">Azioni</th></tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Nessun documento</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`document-row-${d.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{d.title}</td>
                <td className="px-4 py-3 text-slate-600">{d.category}</td>
                {!trustId && <td className="px-4 py-3 text-slate-600">{trustName(d.trust_id)}</td>}
                <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{d.file_name} <span className="text-slate-400">· {kb(d.size)}</span>
                  {d.scan_status && <span data-testid={`document-scan-${d.id}`} className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${d.scan_status === "pulito" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : d.scan_status === "infetto" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{d.scan_status === "pulito" ? "Antivirus OK" : d.scan_status === "infetto" ? "In quarantena" : "Da analizzare"}</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs">v{d.version}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(d.created_at)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {trash ? (
                    <Button size="sm" variant="outline" data-testid={`document-restore-${d.id}`} onClick={() => act(() => api.post(`/documents/${d.id}/restore`), "Documento ripristinato")}><RotateCcw className="h-4 w-4 mr-1" />Ripristina</Button>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" title="Scarica" data-testid={`document-download-${d.id}`} onClick={() => downloadFile(`/documents/${d.id}/download`, d.file_name).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Versioni" data-testid={`document-versions-${d.id}`} onClick={() => api.get(`/documents/${d.id}/versions`).then((r) => setVersions({ doc: d, list: r.data }))}><History className="h-4 w-4" /></Button>
                      {hasRole("admin", "gestore_utenti", "collaboratore") && d.file_name?.toLowerCase().endsWith(".pdf") && <Button variant="ghost" size="icon" title="Invia in firma (DocuSign)" data-testid={`document-sign-${d.id}`} onClick={() => setSignDoc(d)}><PenLine className="h-4 w-4" /></Button>}
                      {can("documents", "u") && <Button variant="ghost" size="icon" title="Nuova versione" data-testid={`document-new-version-${d.id}`} onClick={() => { setVerTarget(d); verRef.current?.click(); }}><Upload className="h-4 w-4" /></Button>}
                      {can("documents", "d") && <Button variant="ghost" size="icon" title="Elimina" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`document-delete-${d.id}`} onClick={() => window.confirm("Spostare il documento nel cestino?") && act(() => api.delete(`/documents/${d.id}`), "Documento spostato nel cestino")}><Trash2 className="h-4 w-4" /></Button>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white" data-testid="document-upload-dialog">
          <DialogHeader><DialogTitle className="font-heading">Carica documento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!trustId && (
              <select className={inputClass} value={form.trust_id || ""} onChange={(e) => setForm({ ...form, trust_id: e.target.value })} data-testid="document-trust-select">
                <option value="">— Trust (obbligatorio per profili non amministrativi) —</option>
                {(refs.trusts || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <input className={inputClass} placeholder="Titolo" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="document-title-input" />
            <select className={inputClass} value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="document-category-select">
              <option value="">— Categoria —</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input type="file" ref={fileRef} className="block w-full text-sm" data-testid="document-file-input" />
            <p className="text-xs text-slate-500">Formati: PDF, Word, Excel, immagini, P7M · max 25 MB. Ogni file viene analizzato dall'antivirus e registrato con impronta SHA-256.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button disabled={busy} onClick={upload} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="document-upload-submit">{busy ? "Caricamento…" : "Carica"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versions} onOpenChange={(o) => !o && setVersions(null)}>
        <DialogContent aria-describedby={undefined} className="bg-white max-w-2xl" data-testid="document-versions-dialog">
          <DialogHeader><DialogTitle className="font-heading">Versioni — {versions?.doc.title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {versions?.list.map((v) => (
              <div key={v.n} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">v{v.n} · {v.file_name}</p>
                  <p className="text-xs text-slate-500 font-mono truncate">SHA-256 {v.sha256}</p>
                  <p className="text-xs text-slate-400">{fmtDateTime(v.uploaded_at)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadFile(`/documents/${versions.doc.id}/download?version=${v.n}`, v.file_name)}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
